import React, { useState } from 'react';
import noop from 'lodash/noop';
import styled, { css } from 'styled-components';
import {
  Box,
  Button,
  ChoiceBox,
  ChoiceboxGroup,
  FormDropdown,
  FormDropdownOption,
  Modal,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import { IneligibleAccountWarning } from 'journey/productDetails/components/ProductConfigurator/components/WearableEligibilityActionLayerContent/component/WearableEligibilityAccountSelector/components/IneligibleAccountWarning';
import { useSegment } from 'shared/hooks';

export const ModalContainer = styled.div`
  ${({ theme: { media } }) => css`
    display: flex;
    flex-direction: column;
    min-width: 327px;
    height: auto;

    ${media.up('slim')} {
      width: 376px;
    }

    ${media.up('big')} {
      width: 392px;
    }
  `}
`;

export const StyledFormDropdown = styled(FormDropdown)`
  & .spark__menu-list {
    max-height: 168px;
  }
`;

export interface BillingAccount {
  accountNumber: string;
  accountName: string;
  isEligible?: boolean;
}

export interface SelectorProps {
  selectedAccountNumber: string;
  setSelectedAccountNumber: (accountNumber: string) => void;
  accountList: BillingAccount[];
  isSamsungWearable?: boolean;
}

const PREPAID_ACCOUNT = 'prepaid-account';

export function DropDownSelector({
  selectedAccountNumber,
  setSelectedAccountNumber,
  accountList,
  isSamsungWearable,
}: SelectorProps) {
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const options = accountList.map(({ accountName, accountNumber }) => ({
    label: `${accountName} - #${accountNumber}`,
    value: accountNumber || PREPAID_ACCOUNT,
  }));

  const value = {
    label: options.find((o) => o.value === selectedAccountNumber)?.label || '',
    value: selectedAccountNumber ?? '',
  };

  const handleOnChange = (accountNumber: string): void => {
    setSelectedAccountNumber(accountNumber);

    const isEligible =
      Boolean(
        !accountList.find(
          (account) =>
            (account.accountNumber || PREPAID_ACCOUNT) === accountNumber,
        )?.isEligible,
      ) && isSamsungWearable;

    setShowWarningMessage(isEligible);
  };

  return (
    <Box pt="s3" pb="s4">
      <StyledFormDropdown
        value={value}
        options={options}
        onChange={(option: FormDropdownOption<string>) => {
          handleOnChange(option.value);
        }}
        placeholder="Select"
      />
      {showWarningMessage ? <IneligibleAccountWarning /> : null}
    </Box>
  );
}

export function ChoiceBoxSelector({
  selectedAccountNumber,
  setSelectedAccountNumber,
  accountList,
  isSamsungWearable,
}: SelectorProps) {
  const { isBusiness } = useSegment();

  return (
    <Box pt="s2" pb="s3">
      <ChoiceboxGroup>
        {accountList.map(
          ({ accountName, accountNumber, isEligible }, index) => {
            const showWarningMessage =
              !isEligible &&
              selectedAccountNumber === (accountNumber || PREPAID_ACCOUNT) &&
              isSamsungWearable;
            return (
              <>
                {accountNumber ? (
                  <ChoiceBox
                    key={accountNumber}
                    name={accountNumber}
                    value={selectedAccountNumber ?? ''}
                    checked={selectedAccountNumber === accountNumber}
                    onChange={() => {
                      setSelectedAccountNumber(accountNumber);
                    }}
                  >
                    <div>
                      {isBusiness ? null : (
                        <Text variant="bodySDemi">{accountName}</Text>
                      )}
                      <Text variant="bodySDemi">#{accountNumber}</Text>
                    </div>
                  </ChoiceBox>
                ) : (
                  <ChoiceBox
                    key={PREPAID_ACCOUNT}
                    name={PREPAID_ACCOUNT}
                    value={PREPAID_ACCOUNT}
                    checked={selectedAccountNumber === PREPAID_ACCOUNT}
                    onChange={() => {
                      setSelectedAccountNumber(PREPAID_ACCOUNT);
                    }}
                  >
                    <Text variant="bodySDemi">PREPAID ACCOUNT</Text>
                  </ChoiceBox>
                )}
                {showWarningMessage ? (
                  <IneligibleAccountWarning
                    index={index}
                    accountList={accountList}
                  />
                ) : null}
              </>
            );
          },
        )}
      </ChoiceboxGroup>
    </Box>
  );
}

interface AccountSelectorModalProps {
  accountList: BillingAccount[];
  isOpen: boolean;
  onContinueClick: (accountNumber: string) => void;
}

function AccountSelectorModal({
  accountList,
  isOpen,
  onContinueClick,
}: AccountSelectorModalProps) {
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<
    string | null
  >(null);

  const hasMoreThanFiveAccounts = accountList.length > 5;

  const selectorProps = {
    selectedAccountNumber,
    setSelectedAccountNumber,
    accountList,
  };

  return (
    <Modal onClose={noop} isOpen={isOpen} showCloseButton={false}>
      <ModalContainer>
        <Text variant="headingS">Select an account</Text>
        <LinkRegion isNotTracked>
          {hasMoreThanFiveAccounts ? (
            <DropDownSelector {...selectorProps} />
          ) : (
            <ChoiceBoxSelector {...selectorProps} />
          )}
        </LinkRegion>
        <LinkRegion regionId="productDetails.selectAccount">
          <Button
            variant="primary"
            onClick={() => {
              onContinueClick(selectedAccountNumber);
            }}
            disabled={!selectedAccountNumber}
          >
            Continue
          </Button>
        </LinkRegion>
      </ModalContainer>
    </Modal>
  );
}

export default AccountSelectorModal;
